import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import Button from "./button.jsx";

const TestQuestion = ({ exercise, removeInfo }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ID = params.get("id");

  const progressKey = `exercise_progress_${ID}`;
  const pointsKey = `exercise_points_${ID}`;

  const [currentQuestion, setCurrentQuestion] = useState(
    Number(localStorage.getItem(progressKey)) || 1
  );
  const [points, setPoints] = useState(
    Number(localStorage.getItem(pointsKey)) || 0
  );

  const [question, setQuestion] = useState(null);
  const [sentence, setSentence] = useState([]);
  const [isFinished, setIsFinished] = useState(false);

  const [selectedIndices, setSelectedIndices] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [userInputs, setUserInputs] = useState({});
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  useEffect(() => {
    if (currentQuestion > exercise.questions.length) {
      setIsFinished(true);
      return;
    }

    const q = exercise.questions[currentQuestion - 1];
    if (!q) return;

    setQuestion(q);
    setSentence(q.sentence || []);
    setSelectedIndices([]);
    setCorrectAnswers([]);
    setWrongAnswers([]);
    setUserInputs({});
    setAnswerSubmitted(false);
  }, [exercise, currentQuestion]);

  const handleSelection = (_, idx) => {
    if (answerSubmitted) return;
    setSelectedIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const handleInputChange = (idx, value) => {
    setUserInputs((prev) => ({ ...prev, [idx]: value }));
  };

  const [selectedKasus, setSelectedKasus] = useState(null);

  const submitAnswerMC = (kasus) => {
    if (!question || answerSubmitted) return;

    setAnswerSubmitted(true);
    setSelectedKasus(kasus);

    const isCorrect = kasus === question.correct_answer.kasus;

    if (isCorrect) {
      updatePoints();
    }
  };

  const submitAnswer = () => {
    if (!question || answerSubmitted) return;
    setAnswerSubmitted(true);

    if (Array.isArray(sentence)) {
      const correctSet = new Set(question.correct_answer);
      const allCorrectIndices = sentence
        .map((word, idx) => (correctSet.has(word) ? idx : null))
        .filter((i) => i !== null);

      const wrongClicked = selectedIndices.filter((idx) => !correctSet.has(sentence[idx]));
      setCorrectAnswers(allCorrectIndices);
      setWrongAnswers(wrongClicked);

      const missedCorrect = allCorrectIndices.filter((idx) => !selectedIndices.includes(idx));

      if (wrongClicked.length === 0 && missedCorrect.length === 0) {
        updatePoints();
      }
    } else {
      const results = question.correct_answer.map((ans, idx) => {
        const userValue = (userInputs[idx] || "").trim().toLowerCase();
        const correctValue = ans.base.toLowerCase();
        return userValue === correctValue;
      });

      if (results.every((res) => res === true)) {
        updatePoints();
      }
    }
  };

  const updatePoints = () => {
    const newPoints = points + 1;
    setPoints(newPoints);
    localStorage.setItem(pointsKey, newPoints);
  };

  const nextQuestion = () => {
    if (currentQuestion < exercise.questions.length) {
      const next = currentQuestion + 1;
      setCurrentQuestion(next);
      localStorage.setItem(progressKey, next);
    } else {
      localStorage.removeItem(progressKey);
      setCurrentQuestion(0);
      setIsFinished(true);
    }
  };

  if (isFinished) {
    return (
      <div className="test-container finish-screen">
        <h2>Übung beendet!</h2>
        <p>Gut gemacht! Du hast alle Fragen beantwortet.</p>
        <h2 style={{marginTop: "30px"}}>
          Ergebnis: {points} / {exercise.questions.length} Punkte
        </h2>
        <div onClick={removeInfo}>
          <Button className="button-yellow" text="Zurück zur Übersicht" />
        </div>
      </div>
    );
  }

  if (question && question.question_type === "Kasus bestimmen") {
    return (
      <div className="test-container">
        <h2>Frage {currentQuestion} von {exercise.questions.length}</h2>
        <p className="question-text">{question?.question}</p>

        {Array.isArray(sentence) &&
          <p className="test-sentence-kasus">
            {sentence.map((word, idx) => {

              const style = {
                padding: idx === (sentence.length - 1) ? "2px 0px" : "2px 4px",
                marginRight: idx === (sentence.length - 2) ? "0px" : "4px",
                background: word === question.correct_answer.noun ? "rgba(231, 193, 116, 1)" : "",
                borderRadius: "4px"
              }

              return (
                <span
                key={idx}
                style={style}
                >{word}</span>
              );
            })}
          </p>
        }

        <hr style={{ margin: "20px 0" }} />

          <div className="answer-grid">
            {["Nominativ", "Akkusativ", "Dativ", "Genitiv"].map((kasus) => {
              const isCorrect = kasus === question.correct_answer.kasus;
              const isSelected = selectedKasus === kasus;
              
              let bgColor = "";
              if (answerSubmitted) {
                if (isCorrect) bgColor = "green";
                if (isSelected && !isCorrect) bgColor = "red";
              }

              return (
                <div 
                  key={kasus}
                  onClick={() => submitAnswerMC(kasus)}
                  style={{
                    cursor: answerSubmitted ? "default" : "pointer",
                    backgroundColor: bgColor,
                    transition: "background 0.3s ease",
                  }}
                >
                  <p>{kasus}</p>
                </div>
              );
            })}
          </div>

        {answerSubmitted &&

          <div onClick={nextQuestion}>
            <Button 
              className="button-yellow" 
              text={currentQuestion === exercise.questions.length ? "Übung beenden" : "Nächste Frage"} 
            />
          </div>

        }

        <p style={{ marginTop: "20px", fontWeight: "bold" }}>Punkte: {points}</p>

      </div>
    );
  }

  return (
    <div className="test-container">
      <h2>Frage {currentQuestion} von {exercise.questions.length}</h2>
      <p className="question-text">{question?.question}</p>

      {Array.isArray(sentence) && (
        <p className="test-sentence">
          {sentence.map((word, idx) => {
            const isSelected = selectedIndices.includes(idx);
            const isCorrect = correctAnswers.includes(idx);
            const isWrong = wrongAnswers.includes(idx);
            const isMissed = isCorrect && !isSelected;
            const isFound = isCorrect && isSelected;

            const style = {
              padding: "2px 4px",
              marginRight: "4px",
              cursor: answerSubmitted ? "default" : "pointer",
              borderRadius: "4px",
              display: "inline-block",
              background: answerSubmitted
                ? isWrong ? "red" : isFound ? "green" : isMissed ? "#ffd700" : "transparent"
                : isSelected ? "rgba(231, 193, 116, 1)" : "transparent",
              transition: "background 0.2s ease",
              color: (answerSubmitted && (isWrong || isFound)) ? "white" : "black"
            };

            if (idx < (sentence.length - 1)) {
              return (
                <span key={idx} style={style} onClick={() => handleSelection(word, idx)}>
                  {word}
                </span>
              );
            } else {
              return (
                <span key={idx} style={{marginLeft: "-4px"}}>.</span>
              )
            }
          })}
        </p>
      )}

      {!Array.isArray(sentence) && (
        <div className="input-mode-container">
          <p className="test-sentence" style={{ marginBottom: "20px" }}>
            {sentence}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
            {question?.correct_answer.map((ans, idx) => {
              const userVal = (userInputs[idx] || "").trim().toLowerCase();
              const isCorrect = userVal === ans.base.toLowerCase();
              return (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <label>Grundform {idx + 1}:</label>
                  <input
                    type="text"
                    value={userInputs[idx] || ""}
                    disabled={answerSubmitted}
                    onChange={(e) => handleInputChange(idx, e.target.value)}
                    placeholder="..."
                    style={{
                      padding: "8px",
                      borderRadius: "5px",
                      border: answerSubmitted 
                        ? (isCorrect ? "2px solid green" : "2px solid red") 
                        : "1px solid #ccc",
                    }}
                  />
                  {answerSubmitted && !isCorrect && (
                    <span style={{ color: "green", fontWeight: "bold" }}>➜ {ans.base}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <hr style={{ margin: "20px 0" }} />

      {!answerSubmitted ? (
        <div onClick={submitAnswer}>
          <Button className="button-yellow" text="Antworten" />
        </div>
      ) : (
        <div onClick={nextQuestion}>
          <Button 
            className="button-yellow" 
            text={currentQuestion === exercise.questions.length ? "Übung beenden" : "Nächste Frage"} 
          />
        </div>
      )}

      <p style={{ marginTop: "20px", fontWeight: "bold" }}>Punkte: {points}</p>
    </div>
  );
};

export default TestQuestion;